import { Component, inject, signal, WritableSignal, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from "@openng/optimus-ui/dynamicdialog";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { TableModule } from "@openng/optimus-ui/table";
import { SvgIconComponent } from "../../../../shared/components/svg-icon/svg-icon.component";
import { UserService } from "../../../../services/api/user/user.service";
import { UserComponent } from "../forms/user/user.component";
import { ConfirmationService, MessageService } from "@openng/optimus-ui/api";
import { PermissionService } from "../../../../services/api/permission/permission.service";
import { HasPermissionDirective } from "../../../../shared/directives/has-permission/has-permission.directive";
import { User } from "../../../../models/api/user";
import { Column } from "../../../../models/ui/tableColumn";
import { first } from "rxjs";
import { USER_ROLES } from "../../../../enums/userRoles.enum";

@Component({
  standalone: true,
  selector: "app-user-list",
  imports: [TableModule, SvgIconComponent, TranslateModule, HasPermissionDirective],
  templateUrl: "./user-list.component.html",
  styleUrl: "./user-list.component.scss",
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [],
})
export class UserListComponent implements OnInit {
  private config = inject(DynamicDialogConfig);
  private userFormRef: DynamicDialogRef | null = null;
  private translate = inject(TranslateService);
  private userService = inject(UserService);
  private dialogService = inject(DialogService);
  private message = inject(MessageService);
  private confirmation = inject(ConfirmationService);
  public permissions = inject(PermissionService);
  private customerId: string | undefined;
  private tenantId: string | undefined;
  public users: WritableSignal<User[] | null> = signal(null);
  public columns: Column[] = [
    {
      field: "email",
      header: "gate_administration.user_list.user",
    },
  ];

  public ngOnInit() {
    this.customerId = this.config.data.customerId;
    this.tenantId = this.config.data.tenantId;
    this.getUsers(this.customerId);

    // if no customer id, user list is for whole tenant so add these columns
    if (!this.customerId) {
      this.columns.push({
        field: "role",
        header: "gate_administration.user_list.role",
      });
    }
  }

  /**
   * Retrieves users for a given customer and assigns them to the users property.
   * @param customerId The ID of the customer to retrieve users for.
   */
  private getUsers(customerId?: string) {
    // If there is no customer ID, retrieve all users for tenant
    if (customerId) {
      this.userService
        .getUsersByCustomer(customerId)
        .pipe(first())
        .subscribe({
          next: (users: User[]) => {
            this.users.set(users);
          },
        });
    } else {
      this.userService
        .getUsers()
        .pipe(first())
        .subscribe({
          next: (users: User[]) => {
            this.users.set(
              users.filter(
                (user) =>
                  user.role !== USER_ROLES.TENANT_ADMIN &&
                  user.role !== USER_ROLES.CUSTOMER_ADMIN &&
                  user.role !== USER_ROLES.CUSTOMER_USER
              )
            );
          },
        });
    }
  }

  /**
   * Opens a dialog to either create a new user or edit an existing one.
   * Assigns the dialog result to the users property.
   * @param actionType The type of action to perform. Can be either "add" or "edit".
   * @param user The user to edit, or null to create a new user.
   * @returns A subscription that resolves when the dialog is closed.
   */
  private openDialog(actionType: string, user: User | null) {
    this.userFormRef = this.dialogService.open(UserComponent, {
      header: this.translate.instant("gate_administration.user_list." + actionType),
      modal: true,
      width: "48vw",
      contentStyle: { overflow: "auto" },
      breakpoints: {
        "960px": "75vw",
        "640px": "90vw",
      },
      data: {
        user: user,
        customerId: this.customerId,
        actionType,
        tenantId: this.tenantId,
      },
      closable: true,
    });
    this.userFormRef?.onClose.subscribe({
      next: (success: boolean) => {
        if (success) {
          this.getUsers(this.customerId);

          this.message.add({
            key: "global",
            severity: "success",
            summary: this.translate.instant("common.success"),
            detail: this.translate.instant("gate_administration.user_list.success." + actionType),
            life: 4500,
          });
        }
      },
    });
  }

  /**
   * Opens a dialog to create a new user.
   */
  public onCreateClick() {
    this.openDialog("add", null);
  }

  /**
   * Opens a dialog to edit an existing user.
   * @param user The user to be edited.
   */
  public onEditClick(user: User) {
    this.openDialog("edit", user);
  }

  /**
   * Deletes a user.
   * @param user The user to be deleted.
   * @param event The event that triggered the deletion.
   */
  public onDeleteClick(user: User, event: Event) {
    this.confirmation.confirm({
      target: event.currentTarget as EventTarget,
      message: this.translate.instant("gate_administration.asset_table.confirm"),
      key: "global",
      accept: () => {
        if (user.id) {
          this.userService.deleteUser(user.id).subscribe({
            next: () => {
              this.getUsers(this.customerId);
            },
          });
        }
      },
    });
  }
}
